// eslint-disable-next-line no-use-before-define
import React, { Fragment } from 'react';
import './ClinicList.scss';
import moment from 'moment';
import classNames from 'classnames';
import { StandardDialog } from 'shared-components/components';
import { BaseDatePicker } from 'shared-components/components/FormFields';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import PhotoWidget from 'shared-components/components/UIFormComponents/PhotoWidget';
import { StyledTile } from '../Dashboard';
import { styled } from '@mui/system';
import PatientTooltip from '../PatientTooltip';
import dayjs from 'op-utils/dayjs';
import { Stack, IconButton, Box, Button } from '@mui/material';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { TodayButton } from '../DailyAppointments/RODailyAppointment';
import { useQuery } from '@apollo/client';
import { TENANT_CONFIG_QUERY } from '../../../../components/RO/Base/TenantConfigQuery';

const QUEUED_NFA_TITLE = 'Remove patient record?';
const QUEUED_NFA_BODY =
  ' This will remove the patient record from the Clinic List. Only proceed if patient will not be receiving Radiotherapy at the present time';

interface Patient {
  fullName: string;
  ida: string;
  id: string;
  photoUrl: string;
  age: string;
  dob: string;
  gender: string;
  address: {
    formattedAddress: string;
  };
  emrInstance: string;
}

interface Location {
  name: string;
}

interface AppointmentObject {
  id: string;
  startTime: string;
  duration: number;
  patient: Patient | null;
  appointmentType: string;
  description: string;
  shortDescription?: string | null;
  location: Location;
  department: Location;
  queuedPatient: boolean;
  noFurtherAction: boolean;
}

interface Props extends RouteComponentProps {
  appointments: AppointmentObject[];
  currentDate: string;
  onDateChange: (date: string) => void;
  onNFAClick: () => void;
  nfaWarning: boolean;
  onShowNFAModal: (appointment?: AppointmentObject) => void;
  currAppointment?: AppointmentObject | null | undefined;
}
interface TableProps {
  tableHeaders: string[];
  colWidths: string[];
  appointments: any[];
  onShowNFAModal: (appointment?: AppointmentObject) => void;
  appointmentDescriptionSource?: string | null;
  // isActive?: boolean;
}

const StyledRow = styled('tr')`
  &:hover {
    background-color: white !important;
    cursor: default !important;
  }
`;

const InnerTable = ({
  tableHeaders,
  colWidths,
  appointments,
  onShowNFAModal,
  appointmentDescriptionSource,
}: TableProps) => {
  return (
    <Fragment>
      <table className="resp-table">
        <thead>
          <tr>
            {tableHeaders.map((tableHeader, index) => {
              return (
                <th key={`clinic-list-header-${index}`} style={{ width: colWidths[index] }}>
                  <div
                    className={classNames('header-style', {
                      'header-right-aligned': tableHeader === 'Last Modified',
                    })}>
                    {tableHeader}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {appointments.map((appointment: AppointmentObject) => {
            return (
              <StyledRow
                key={`appointment-${appointment.id}`}
                className={classNames({ nfaStyle: appointment.noFurtherAction })}>
                <td>
                  {appointment.startTime && !appointment.queuedPatient
                    ? moment(appointment.startTime).format('hh:mm A')
                    : '-'}
                </td>
                <td>
                  {!appointment.queuedPatient ? `${appointment.duration && appointment.duration / 60} mins` : '-'}
                </td>
                <td data-test-data={appointment.patient?.fullName.toLowerCase()}>
                  {appointment.patient ? (
                    <PatientTooltip patient={appointment.patient}>
                      <Box>
                        <div className={'patient-name-col'} id={`patient-${appointment.patient.id}`}>
                          <div className="photo-widget">
                            <PhotoWidget patientId={appointment.patient.id} enableOverlay={true} size={'sm'} />
                          </div>
                          <div className={'patient-name-col-fullname-ida'}>
                            <div>
                              <a href={`/radiation/patient/${appointment.patient.id}/summary`}>
                                {appointment.patient.fullName}
                              </a>
                            </div>
                            <div>{appointment.patient.ida}</div>
                          </div>
                        </div>
                      </Box>
                    </PatientTooltip>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {!appointment.queuedPatient
                    ? (appointmentDescriptionSource === 'short_description'
                        ? appointment.shortDescription
                        : appointment.description) || appointment.appointmentType
                    : 'Queued'}
                </td>
                <td>{!appointment.queuedPatient && appointment.department ? appointment.department.name : '-'}</td>
                <td>
                  <div className="action-buttons">
                    <Button
                      id={`remove-button-${appointment.id}`}
                      onClick={(e): void => {
                        e.stopPropagation();
                        onShowNFAModal(appointment);
                      }}>
                      {!appointment.queuedPatient ? 'No Further Action' : 'Remove Patient'}
                    </Button>
                  </div>
                </td>
              </StyledRow>
            );
          })}
        </tbody>
      </table>
    </Fragment>
  );
};

const ROClinicListTable = InnerTable;

const ROClinicList = ({
  appointments,
  currentDate,
  onDateChange,
  onNFAClick,
  nfaWarning,
  onShowNFAModal,
  currAppointment,
}: Props) => {
  const { data: tenantConfigData } = useQuery(TENANT_CONFIG_QUERY);
  const appointmentDescriptionSource = tenantConfigData?.tenantConfig?.appointmentDescriptionSource;
  const tableHeaders = ['Time', 'Duration', 'Patient name', 'Appointment type', 'Location', 'Actions'];
  const colWidths = ['10%', '6%', '18%', '16%', '18%', '30%'];
  const currDate = currentDate ? moment(currentDate).toDate() : null;

  return (
    <Fragment>
      <StandardDialog
        open={nfaWarning}
        onClose={() => {
          onShowNFAModal();
        }}
        title={QUEUED_NFA_TITLE}
        destructiveSubmit
        submitText={'Remove record'}
        onSubmit={() => {
          onNFAClick();
        }}>
        <span id="modal-info-body" style={{ lineHeight: 1.5 }}>
          {QUEUED_NFA_BODY}
        </span>
      </StandardDialog>
      <div className="RO-dashboard-wrapper">
        <StyledTile className="clinicList-dashboard-wrapper">
          <div className="filter-wrapper">
            <div className="table-title-style">
              <span className="table-title" data-test-id="table-title">
                Clinic List
              </span>
            </div>
            <div className="table-filters">
              <Stack direction="row" alignItems="center" sx={{ marginLeft: 'auto', marginBottom: '6px' }} gap="2px">
                <TodayButton
                  onClick={() => {
                    onDateChange(moment().format('YYYY-MM-DD'));
                  }}>
                  Today
                </TodayButton>
                <IconButton
                  onClick={() =>
                    onDateChange((currDate ? moment(currDate).subtract(1, 'days') : moment()).format('YYYY-MM-DD'))
                  }>
                  <ArrowBackIosNew color="primary" />
                </IconButton>
                <BaseDatePicker
                  id="dailyAppointmentDatePicker"
                  value={dayjs(currDate)}
                  onChange={(date, context) => {
                    if (context.validationError || !date) return;
                    onDateChange(date.format('YYYY-MM-DD'));
                  }}
                />
                <IconButton
                  onClick={() =>
                    onDateChange((currDate ? moment(currDate).add(1, 'days') : moment()).format('YYYY-MM-DD'))
                  }>
                  <ArrowForwardIos color="primary" />
                </IconButton>
              </Stack>
            </div>
          </div>
          <div className="table-wrapper">
            <ROClinicListTable
              tableHeaders={tableHeaders}
              colWidths={colWidths}
              appointments={appointments}
              onShowNFAModal={onShowNFAModal}
              appointmentDescriptionSource={appointmentDescriptionSource}
            />
          </div>
        </StyledTile>
      </div>
    </Fragment>
  );
};

export default withRouter(ROClinicList);
